import { json, type LoaderFunctionArgs } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { useEffect } from "react";
import { clearClientAuthStateOnLogout } from "#app/utils/auth.client";
import { localLogout } from "#app/utils/auth.server.ts";

/** After logout, send user to landing (/) which has the SSO "Try For Free" button – avoids login page and OAuth2 issues. */
const REDIRECT_AFTER_LOGOUT = "/";

/**
 * GET /force-logout?redirectTo=/
 * Optional: ?destroy=1 — skip client clear step (caller already cleared).
 *
 * Without destroy=1: clears workflow token + org/team client state (same as POST /logout),
 * then full localStorage, then redirects with destroy=1. With destroy=1: destroys session and
 * redirects to redirectTo (no Keycloak logout page).
 */
export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const redirectTo =
    url.searchParams.get("redirectTo")?.trim() || REDIRECT_AFTER_LOGOUT;

  if (url.searchParams.get("destroy") === "1") {
    return localLogout({ request, redirectTo });
  }

  return json({ redirectTo });
}

export default function ForceLogoutRoute() {
  const { redirectTo } = useLoaderData<typeof loader>();

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      clearClientAuthStateOnLogout();
      window.localStorage.clear();
    } catch {
      // ignore
    }
    window.location.href = `/force-logout?destroy=1&redirectTo=${encodeURIComponent(redirectTo)}`;
  }, [redirectTo]);

  return (
    <div className="flex min-h-[200px] items-center justify-center text-muted-foreground">
      Signing out…
    </div>
  );
}
